// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, getReferenceString, normalizeErrorString } from '@medplum/core';
import type { CareTeam, Patient, Practitioner } from '@medplum/fhirtypes';
import { AsyncAutocomplete, useMedplum } from '@medplum/react';
import { IconPlus, IconSearch, IconUserPlus } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { TableAnchor } from '../../../components/layout/TableAnchor';
import type { CareTeamMember } from '../../../hooks/useCareTeamMembers';
import { useRole } from '../../../hooks/useRole';
import { hasPermission } from '../../../utils/permissions';
import { withTenantScope } from '../../../utils/tenant';

const PAGE_SIZE = 10;

function formatDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return dayjs(value).format('MM/DD/YYYY');
}

function formatDateTime(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return dayjs(value).format('MM/DD/YYYY [at] h:mma');
}

export interface CareTeamSectionProps {
  patient: Patient;
  careTeam: CareTeam | undefined;
  members: CareTeamMember[];
  loading: boolean;
  onCareTeamChanged: (team: CareTeam) => void;
  onRefresh: () => void;
}

export function CareTeamSection({
  patient,
  careTeam,
  members,
  loading,
  onCareTeamChanged,
  onRefresh,
}: CareTeamSectionProps): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { role, isAdmin } = useRole();
  const canEdit = hasPermission({ role, permission: 'edit-patient-profile', isAdmin });

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState<Practitioner | undefined>();
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return members;
    }
    return members.filter((m) => `${m.name} ${m.role}`.toLowerCase().includes(q));
  }, [members, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paginated = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const loadPractitioners = async (input: string, signal: AbortSignal): Promise<Practitioner[]> => {
    try {
      return await medplum.searchResources('Practitioner', { name: input, _count: '10' }, { signal });
    } catch (err) {
      if (!signal.aborted) {
        console.error('Practitioner search failed', err);
      }
      return [];
    }
  };

  const handleAdd = async (): Promise<void> => {
    if (!selectedPractitioner) {
      return;
    }
    setSaving(true);
    try {
      const participant = {
        member: createReference(selectedPractitioner),
      };

      let team: CareTeam;
      if (careTeam?.id) {
        team = await medplum.updateResource<CareTeam>({
          ...careTeam,
          participant: [...(careTeam.participant ?? []), participant],
        });
      } else {
        team = await medplum.createResource<CareTeam>(
          withTenantScope({
            resourceType: 'CareTeam',
            status: 'active',
            subject: createReference(patient),
            participant: [participant],
          })
        );
      }
      onCareTeamChanged(team);
      onRefresh();
      setAddOpen(false);
      setSelectedPractitioner(undefined);
      showNotification({ color: 'green', message: 'Provider added to care team' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (member: CareTeamMember): Promise<void> => {
    if (!careTeam?.id) {
      return;
    }
    try {
      const memberRef = getReferenceString(member.practitioner);
      const next = await medplum.updateResource<CareTeam>({
        ...careTeam,
        participant: (careTeam.participant ?? []).filter((p) => p.member?.reference !== memberRef),
      });
      onCareTeamChanged(next);
      onRefresh();
      showNotification({ color: 'green', message: 'Provider removed from care team' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    }
  };

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>Care Team</Title>
        <Group gap="sm">
          <TextInput
            placeholder="Search…"
            value={query}
            onChange={(e) => {
              setQuery(e.currentTarget.value);
              setPage(1);
            }}
            leftSection={<IconSearch size={14} />}
            size="sm"
            w={240}
            aria-label="Search care team"
          />
          <Button
            leftSection={<IconUserPlus size={16} />}
            onClick={() => setAddOpen(true)}
            disabled={!canEdit}
            title={!canEdit ? 'You do not have permission to edit the care team' : undefined}
          >
            Add Provider
          </Button>
        </Group>
      </Group>

      {loading ? (
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      ) : (
        <>
          <Box style={{ overflowX: 'auto' }}>
            <Table highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Provider</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Last Interaction</Table.Th>
                  <Table.Th>Next Appointment</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {paginated.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text ta="center" c="dimmed" py="md">
                        No providers on the care team.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  paginated.map((member) => (
                    <Table.Tr key={member.practitioner.id}>
                      <Table.Td>{member.name}</Table.Td>
                      <Table.Td>{member.role}</Table.Td>
                      <Table.Td>{formatDate(member.lastInteraction)}</Table.Td>
                      <Table.Td>{formatDateTime(member.nextAppointment?.start)}</Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Anchor
                            component="button"
                            type="button"
                            size="sm"
                            onClick={() => handleRemove(member)}
                            c="red"
                          >
                            Remove
                          </Anchor>
                          <TableAnchor
                            component="button"
                            type="button"
                            size="sm"
                            onClick={() => navigate(`/Patient/${patient.id}/Task`)}
                          >
                            Create Task
                          </TableAnchor>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Box>

          {filtered.length > PAGE_SIZE && (
            <Group justify="space-between" mt="sm">
              <Text size="sm" c="dimmed">{`${filtered.length} of ${members.length}`}</Text>
              <Pagination value={pageSafe} onChange={setPage} total={totalPages} size="sm" />
            </Group>
          )}
        </>
      )}

      <Modal opened={addOpen} onClose={() => setAddOpen(false)} title="Add Provider" centered>
        <Stack gap="md">
          <AsyncAutocomplete
            placeholder="Search providers…"
            loadOptions={loadPractitioners}
            toOption={(item) => {
              const p = item as Practitioner;
              const label = p.name?.[0]
                ? `${p.name[0].prefix?.[0] ?? ''} ${p.name[0].given?.[0] ?? ''} ${p.name[0].family ?? ''}`.trim()
                : (p.id ?? '');
              return { value: p.id ?? '', label, resource: p };
            }}
            onChange={(items) => setSelectedPractitioner(items[0] as Practitioner | undefined)}
            maxValues={1}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              leftSection={<IconPlus size={14} />}
              onClick={handleAdd}
              loading={saving}
              disabled={!selectedPractitioner}
            >
              Add
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
